import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../../../components/ui/dialog";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../../../components/ui/tabs";
import { Check, X, Minus, AlertTriangle } from "lucide-react";
import { type AuditSession } from "../../../../models/audit/auditSession";
import { type ControlSessionEnvelope } from "../../../../models/audit/controlSession";
import {
  effectiveAttributeId,
  getColumnDisplayName,
} from "../../../../lib/columnNameUtils";
import { getLatestCheckResult } from "../../../../lib/sessionRunUtils";
import { analyticsSummaryToCheckResult } from "../../../../models/audit/analyticsTranslator";
import { type DrySessionSourceRow } from "../../../../models/audit/procedureSessionTableShell";
import { useControl } from "../../../../hooks/audit/useControl";
import { useProcedureSession } from "../../../../hooks/audit/useProcedureSession";
import { useProcedureSessionTableShell } from "../../../../hooks/audit/useProcedureSessionTableShell";
import { LoadingSpinner } from "../../../../components/ui/loadingSpinner/loadingSpinner";
import { Button } from "../../../../components/ui/button";
import {
  analyticsCellResult,
  type VerdictCellResult,
} from "../lib/verdictCellResult";
import { resolveLiveAnalyticsVerdict } from "../lib/liveAnalyticsVerdict";

type NormalizedResult = VerdictCellResult;

/** The table shell's live verdict rows, by source-row id. */
type DryRowsById = Map<string, DrySessionSourceRow>;

/**
 * What the analytics cells have to read their verdicts from.
 *
 * A shell that never arrived is not the same input as a shell holding no
 * verdict for a cell: rows one could not fetch would read as `none` everywhere,
 * and `getAttributeStatusForSample` rolls a `none` up as untested, so an
 * attribute an analytics column has already judged would be reported unrun.
 * `unavailable` says what is true instead — the verdicts stand and cannot be
 * read — which is the answer an unreadable cell already carries.
 *
 * Only analytics columns name this source. Attribute-test and evaluated results
 * are computed from the session alone and are unaffected by it.
 */
type AnalyticsVerdictSource =
  | { kind: "rows"; dryRowsById: DryRowsById }
  | { kind: "unavailable" };

type AttributeStatus =
  | "pass"
  | "issues_found"
  | "requires_attention"
  | "not_applicable"
  | "pending";

interface WorkpaperSummaryDialogProps {
  /**
   * Phase 3: the dialog lazy-fetches the heavy procedure-session tree
   * **only when opened**, gated by the `open` flag. The Phase 3 work-paper
   * view's primary cache is the table-shell + per-row source rows; the
   * summary dialog uniquely needs the full hydrated tree for its
   * cross-row / cross-column aggregation, so it pays for `useProcedureSession`
   * itself, on demand, instead of forcing the parent component to hold it.
   */
  procedureSessionId: string;
  controlSession: ControlSessionEnvelope;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function getResultIcon(result: NormalizedResult | AttributeStatus) {
  switch (result) {
    case "pass":
      return <Check className="h-3.5 w-3.5 text-green-600" />;
    case "fail":
    case "issues_found":
      return <X className="h-3.5 w-3.5 text-red-500" />;
    case "not_applicable":
      return <Minus className="h-3.5 w-3.5 text-stone-400" />;
    case "inconclusive":
    case "requires_attention":
      return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
    case "pending":
      return <span className="text-stone-300">—</span>;
  }
}

/**
 * Get normalized result for a source row + column combination.
 * Handles attribute test, evaluated and analytics columns.
 */
function getNormalizedResult(
  sourceRow: AuditSession["sourceRows"][number],
  column: AuditSession["procedure"]["columns"][number],
  analyticsVerdicts: AnalyticsVerdictSource,
): NormalizedResult {
  // Attribute test column
  if (column.attributeTestColumn) {
    const results = sourceRow.attributeTestResults.filter(
      (r) => r.attributeTestColumnId === column.attributeTestColumn?.id,
    );
    if (results.length === 0) return "pending";
    // Sort by createdAt descending to get the latest result
    const sorted = [...results].sort(
      (a, b) =>
        new Date(b.createdAt ?? 0).getTime() -
        new Date(a.createdAt ?? 0).getTime(),
    );
    const result = sorted[0];
    if (!result.isConclusive) return "inconclusive";
    return result.outcome;
  }

  // Evaluated column
  if (column.evaluatedColumn) {
    const result = getLatestCheckResult(sourceRow, column.evaluatedColumn.id);
    if (!result) return "pending";
    return result.decision;
  }

  // Analytics column. It needs its own arm because `pending` is not a neutral
  // answer: `getAttributeStatusForSample` propagates a single pending cell to
  // the whole attribute row, so an analytics column read as pending reports
  // every attribute it tests as untested for that sample.
  //
  // The verdict is read off the table shell rather than off the session this
  // dialog is otherwise built from. The session's `analyticsResults` is the
  // whole supersession chain, and its heavy cache entry is never invalidated by
  // an override — the override flow refreshes the table shell only, and the
  // cache matches by prefix, so the longer key does not reach the shorter one.
  // Filtering that chain on `supersededAt` would therefore read this client's
  // stale copy of the fact and let an already-overridden verdict decide the
  // cell, while the grid beside it showed the override. The shell's projection
  // carries live rows only and is the entry the override refreshes.
  //
  // A cell holding a verdict it cannot read, or more than one, is inconclusive:
  // it was tested, so `pending` would misreport it, and no single verdict can
  // speak for it, so neither can a pass or a fail. A shell that never arrived
  // leaves every cell in that same position, and answers the same way.
  if (column.analyticsColumn) {
    if (analyticsVerdicts.kind === "unavailable") return "inconclusive";
    const live = resolveLiveAnalyticsVerdict(
      analyticsVerdicts.dryRowsById.get(sourceRow.id),
      column.analyticsColumn.id,
    );
    switch (live.kind) {
      case "none":
        return "pending";
      case "unreadable":
        return "inconclusive";
      case "verdict":
        return analyticsCellResult(
          analyticsSummaryToCheckResult(live.summary, column),
        );
    }
  }

  return "pending";
}

/**
 * Aggregate per-column results into an attribute-level status for a single sample.
 * Priority: issues_found > requires_attention > not_applicable > pending > pass
 */
function getAttributeStatusForSample(
  sourceRow: AuditSession["sourceRows"][number],
  columns: AuditSession["procedure"]["columns"][number][],
  analyticsVerdicts: AnalyticsVerdictSource,
): AttributeStatus {
  const results = columns.map((col) =>
    getNormalizedResult(sourceRow, col, analyticsVerdicts),
  );

  // If no results at all, pending
  if (results.length === 0) return "pending";

  // If any fail → issues_found
  if (results.some((r) => r === "fail")) return "issues_found";

  // If any inconclusive → requires_attention
  if (results.some((r) => r === "inconclusive")) return "requires_attention";

  // If all are not_applicable → not_applicable
  if (results.every((r) => r === "not_applicable")) return "not_applicable";

  // If any pending → pending
  if (results.some((r) => r === "pending")) return "pending";

  // All remaining must be pass (or mix of pass + n/a)
  return "pass";
}

function SampleResultsTab({
  session,
  controlSession,
  analyticsVerdicts,
}: {
  session: AuditSession;
  controlSession: ControlSessionEnvelope;
  analyticsVerdicts: AnalyticsVerdictSource;
}) {
  // Phase 3 bridge: hydrated `attributes` / `keyReportItems` are not on the
  // control-session envelope or table-shell — `useControl` stays at this
  // call site per `instructions.md` ("If `useControl` is still needed at a
  // Phase-3 site, leave it with a clear comment."). A future BE phase may
  // extend the envelope's `control` field; until then this bridge survives.
  const { data: hydratedControl } = useControl(controlSession.controlId);
  const attributes =
    controlSession.workStep?.attributes ?? hydratedControl?.attributes ?? [];

  // Every column that reaches a verdict, in the order the workpaper lays them
  // out. An analytics column is one of them: it tests a sample and reaches a
  // pass, a fail, not applicable or inconclusive, all results this tab already
  // draws, so leaving it out would hide its whole column rather than show it
  // unrun.
  const testableColumns = useMemo(
    () =>
      session.procedure.columns
        .filter(
          (col) =>
            col.attributeTestColumn ||
            col.evaluatedColumn ||
            col.analyticsColumn,
        )
        .sort((a, b) => a.columnOrder - b.columnOrder),
    [session.procedure.columns],
  );

  const sortedRows = useMemo(
    () =>
      [...session.sourceRows].sort(
        (a, b) => (a.sampleNumber ?? 0) - (b.sampleNumber ?? 0),
      ),
    [session.sourceRows],
  );

  if (testableColumns.length === 0) {
    return (
      <p className="text-sm text-stone-500 py-4">No test columns found.</p>
    );
  }

  return (
    <div className="overflow-auto max-h-[60vh]">
      <table className="w-full text-xs border-collapse">
        <thead className="sticky top-0 bg-white z-10">
          <tr>
            <th className="text-left font-medium text-stone-500 px-2 py-1.5 border-b border-stone-200 whitespace-nowrap">
              #
            </th>
            {testableColumns.map((col) => (
              <th
                key={col.id}
                className="font-medium text-stone-500 px-2 py-1.5 border-b border-stone-200 text-center whitespace-nowrap"
              >
                {getColumnDisplayName(
                  col,
                  session.procedure.columns,
                  attributes,
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => (
            <tr key={row.id} className="hover:bg-stone-50">
              <td className="px-2 py-1 border-b border-stone-100 font-medium text-stone-700 whitespace-nowrap">
                {row.sampleNumber ?? "—"}
              </td>
              {testableColumns.map((col) => {
                const result = getNormalizedResult(row, col, analyticsVerdicts);
                return (
                  <td
                    key={col.id}
                    className="px-2 py-1 border-b border-stone-100 text-center"
                  >
                    <span className="inline-flex items-center justify-center">
                      {getResultIcon(result)}
                    </span>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AttributeSummaryTab({
  session,
  controlSession,
  analyticsVerdicts,
}: {
  session: AuditSession;
  controlSession: ControlSessionEnvelope;
  analyticsVerdicts: AnalyticsVerdictSource;
}) {
  // Phase 3 bridge: hydrated `attributes` / `keyReportItems` are not on the
  // control-session envelope or table-shell — `useControl` stays at this
  // call site per `instructions.md` ("If `useControl` is still needed at a
  // Phase-3 site, leave it with a clear comment."). A future BE phase may
  // extend the envelope's `control` field; until then this bridge survives.
  const { data: hydratedControl, isLoading: isControlLoading } = useControl(
    controlSession.controlId,
  );
  const attributes =
    controlSession.workStep?.attributes ?? hydratedControl?.attributes;
  const attributeGroups = useMemo(() => {
    const attrs = attributes ?? [];

    // Group the columns that test an attribute by the attribute they test. An
    // analytics column belongs here for the same reason an attribute-test one
    // does: the procedure column holds the attribute id, whichever kind it
    // wraps, so an attribute can legitimately be tested by either or by both.
    // The id is the effective one: a control column sets `attributeId`, a
    // work-step column sets `workStepAttributeId`.
    const columnsByAttribute = new Map<
      string,
      AuditSession["procedure"]["columns"][number][]
    >();

    for (const col of session.procedure.columns) {
      const attributeId = effectiveAttributeId(col);
      if (!attributeId) continue;
      if (!col.attributeTestColumn && !col.analyticsColumn) continue;
      const existing = columnsByAttribute.get(attributeId) ?? [];
      existing.push(col);
      columnsByAttribute.set(attributeId, existing);
    }

    // Build summary per attribute
    return attrs
      .filter((attr) => columnsByAttribute.has(attr.id))
      .sort((a, b) => a.attributeOrder - b.attributeOrder)
      .map((attr) => {
        const cols = columnsByAttribute.get(attr.id) ?? [];
        const sortedRows = [...session.sourceRows].sort(
          (a, b) => (a.sampleNumber ?? 0) - (b.sampleNumber ?? 0),
        );

        let passCount = 0;
        let issuesCount = 0;
        let attentionCount = 0;
        let naCount = 0;
        let pendingCount = 0;

        for (const row of sortedRows) {
          const status = getAttributeStatusForSample(
            row,
            cols,
            analyticsVerdicts,
          );
          switch (status) {
            case "pass":
              passCount++;
              break;
            case "issues_found":
              issuesCount++;
              break;
            case "requires_attention":
              attentionCount++;
              break;
            case "not_applicable":
              naCount++;
              break;
            case "pending":
              pendingCount++;
              break;
          }
        }

        const total = sortedRows.length;

        return {
          attribute: attr,
          passCount,
          issuesCount,
          attentionCount,
          naCount,
          pendingCount,
          total,
        };
      });
  }, [
    session.procedure.columns,
    session.sourceRows,
    attributes,
    analyticsVerdicts,
  ]);

  // `useControl` loads independently from `useProcedureSession`. Without this
  // gate, the tab would render `attributeGroups = []` (because `attrs = []`
  // until the control resolves) and flash "No attribute results found." before
  // the real summary appears. A work-step session has no `controlId`, so
  // `useControl` stays disabled and the attributes come off the envelope.
  if (
    isControlLoading ||
    (controlSession.controlId !== null && !hydratedControl)
  ) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  if (attributeGroups.length === 0) {
    return (
      <p className="text-sm text-stone-500 py-4">No attribute results found.</p>
    );
  }

  return (
    <div className="space-y-2 max-h-[60vh] overflow-auto">
      {attributeGroups.map(
        ({
          attribute,
          passCount,
          issuesCount,
          attentionCount,
          naCount,
          pendingCount,
          total,
        }) => {
          // Determine overall status icon
          let overallStatus: AttributeStatus = "pass";
          if (issuesCount > 0) overallStatus = "issues_found";
          else if (attentionCount > 0) overallStatus = "requires_attention";
          else if (pendingCount > 0) overallStatus = "pending";
          else if (naCount === total) overallStatus = "not_applicable";

          const testedCount = total - pendingCount;

          return (
            <div
              key={attribute.id}
              data-testid="workpaper-summary-attribute-row"
              className="flex items-start gap-3 px-3 py-2.5 border border-stone-200 rounded-lg"
            >
              <span className="mt-0.5 shrink-0">
                {getResultIcon(overallStatus)}
              </span>

              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2">
                  <span className="text-sm font-medium text-stone-900">
                    {attribute.reference}
                  </span>
                  <span className="text-xs text-stone-500 truncate">
                    {attribute.statement}
                  </span>
                </div>

                {/* Counts row */}
                <div className="flex items-center gap-3 mt-1 text-xs text-stone-500">
                  {passCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-green-600">
                      <Check className="h-3 w-3" />
                      {passCount}/{testedCount}
                    </span>
                  )}
                  {issuesCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-red-500">
                      <X className="h-3 w-3" />
                      {issuesCount}/{testedCount}
                    </span>
                  )}
                  {attentionCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-amber-500">
                      <AlertTriangle className="h-3 w-3" />
                      {attentionCount}/{testedCount}
                    </span>
                  )}
                  {naCount > 0 && (
                    <span className="inline-flex items-center gap-1 text-stone-400">
                      <Minus className="h-3 w-3" />
                      {naCount}
                    </span>
                  )}
                  {pendingCount > 0 && (
                    <span className="text-stone-300">
                      {pendingCount} pending
                    </span>
                  )}
                </div>

                {/* Progress bar */}
                {testedCount > 0 && (
                  <div className="flex h-1.5 rounded-full overflow-hidden bg-stone-100 mt-1.5">
                    {passCount > 0 && (
                      <div
                        className="bg-green-500"
                        style={{
                          width: `${(passCount / testedCount) * 100}%`,
                        }}
                      />
                    )}
                    {issuesCount > 0 && (
                      <div
                        className="bg-red-400"
                        style={{
                          width: `${(issuesCount / testedCount) * 100}%`,
                        }}
                      />
                    )}
                    {attentionCount > 0 && (
                      <div
                        className="bg-amber-400"
                        style={{
                          width: `${(attentionCount / testedCount) * 100}%`,
                        }}
                      />
                    )}
                    {naCount > 0 && (
                      <div
                        className="bg-stone-300"
                        style={{
                          width: `${(naCount / testedCount) * 100}%`,
                        }}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        },
      )}
    </div>
  );
}

export function WorkpaperSummaryDialog({
  procedureSessionId,
  controlSession,
  open,
  onOpenChange,
}: WorkpaperSummaryDialogProps) {
  // Phase 3: heavy hydration is gated on `open`. Closing the dialog does
  // not invalidate the cache entry, so reopening it re-uses cached data.
  const { data: session, isLoading } = useProcedureSession(
    open ? procedureSessionId : undefined,
  );

  // Analytics verdicts come from the table shell, not from the session above.
  // The session's heavy entry is never refreshed by an override, so a verdict
  // an auditor has already replaced would still decide its cell here while the
  // grid behind the dialog showed the replacement. This entry is the one the
  // override invalidates, and its projection carries live rows only.
  const {
    data: tableShell,
    isLoading: isTableShellLoading,
    error: tableShellError,
    refetch: refetchTableShell,
  } = useProcedureSessionTableShell(open ? procedureSessionId : undefined);

  // An errored entry keeps whatever rows an earlier fetch left in it, and an
  // override is what the fetch it lost was most likely carrying, so rows the
  // shell could not confirm are not allowed to decide a cell either.
  const analyticsVerdicts = useMemo<AnalyticsVerdictSource>(
    () =>
      tableShell && !tableShellError
        ? {
            kind: "rows",
            dryRowsById: new Map(
              tableShell.sourceRows.map((row) => [row.id, row]),
            ),
          }
        : { kind: "unavailable" },
    [tableShell, tableShellError],
  );

  // The shell speaks for analytics cells and for nothing else these tabs draw,
  // so a procedure holding no analytics column neither waits on it nor reports
  // it: every result such a procedure shows is computed from the session.
  const hasAnalyticsColumns =
    session?.procedure.columns.some((column) => column.analyticsColumn) ??
    false;
  const isAnalyticsUnavailable =
    hasAnalyticsColumns && analyticsVerdicts.kind === "unavailable";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Test Results Summary</DialogTitle>
          <DialogDescription>
            Overview of test outcomes across all samples and attributes.
          </DialogDescription>
        </DialogHeader>

        {isLoading ||
        !session ||
        (isAnalyticsUnavailable && isTableShellLoading) ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner />
          </div>
        ) : (
          <>
            {isAnalyticsUnavailable && (
              // Retry rather than "reopen": a failed entry stays in the cache
              // and `registerQuery` hands the same one back, so reopening the
              // dialog shows the error again. Only `refetch` clears it.
              //
              // A retry that fails again swaps the spinner back for this
              // banner with nothing else on screen changing, so it announces.
              <div
                role="status"
                className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2"
              >
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
                <p className="flex-1 text-sm text-amber-900">
                  Analytics results couldn&apos;t be loaded, so analytics
                  columns are marked as requiring attention rather than shown
                  with a verdict. Attribute test and evaluated results below are
                  unaffected.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void refetchTableShell()}
                >
                  Try again
                </Button>
              </div>
            )}

            <Tabs defaultValue="samples" className="flex-1 min-h-0">
              <TabsList>
                <TabsTrigger value="samples">Sample Results</TabsTrigger>
                <TabsTrigger value="attributes">Attribute Summary</TabsTrigger>
              </TabsList>

              <TabsContent value="samples" className="mt-3">
                <SampleResultsTab
                  session={session}
                  controlSession={controlSession}
                  analyticsVerdicts={analyticsVerdicts}
                />
              </TabsContent>

              <TabsContent value="attributes" className="mt-3">
                <AttributeSummaryTab
                  session={session}
                  controlSession={controlSession}
                  analyticsVerdicts={analyticsVerdicts}
                />
              </TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
